/**
 * GoogleCalendarEnrichmentProvider — fetches event titles directly from
 * Google Calendar's REST API using a short-lived OAuth access token.
 *
 * The token is minted server-side (callable: googleCalendarMintAccessToken)
 * and handed to the browser. Google's response goes straight to the
 * browser; our infrastructure never sees event content post-token-mint.
 * See docs/calendar-cache-privacy-plan.md for the full rationale.
 *
 * Test note: the implementation accepts an injectable `fetch` and `baseUrl`
 * so unit tests can spy on the network surface without standing up an HTTP
 * server. The e2e harness later swaps `baseUrl` to point at the in-process
 * mock-google-calendar-server.
 */

const DEFAULT_BASE_URL = "https://www.googleapis.com";

/**
 * @param {Response} response
 * @returns {import('./types').EnrichmentErrorCode}
 */
function classifyError(response) {
  if (response.status === 401) return "permission_denied";
  if (response.status === 403) {
    // Google returns 403 for both "scope missing" (permission) and
    // "rateLimitExceeded" / "userRateLimitExceeded" (rate). We can't read
    // the body without consuming it, so the caller decides — but we lean
    // permission_denied for 403 unless the body explicitly says rate.
    return "permission_denied";
  }
  if (response.status === 404) return "not_found";
  if (response.status === 429) return "rate_limited";
  return "network";
}

async function classifyErrorWithBody(response) {
  if (response.status === 403) {
    try {
      const body = await response.clone().json();
      const reason = body?.error?.errors?.[0]?.reason || "";
      if (reason === "rateLimitExceeded" || reason === "userRateLimitExceeded") {
        return "rate_limited";
      }
    } catch {
      /* fall through to default 403 classification */
    }
  }
  return classifyError(response);
}

function authHeader(accessToken) {
  return { Authorization: `Bearer ${accessToken}` };
}

/**
 * @param {{ fetch?: typeof fetch, baseUrl?: string }} [opts]
 * @returns {import('./types').EnrichmentProvider}
 */
export function createGoogleCalendarEnrichmentProvider(opts = {}) {
  const fetchImpl = opts.fetch || globalThis.fetch;
  const baseUrl = opts.baseUrl || DEFAULT_BASE_URL;

  async function enrichByIds({ calendarId, eventIds, credentials, signal }) {
    const result = new Map();
    if (!eventIds || eventIds.length === 0) return result;
    if (!credentials?.accessToken) {
      const fetchedAt = Date.now();
      for (const eventId of eventIds) {
        result.set(eventId, { title: null, fetchedAt, error: "permission_denied" });
      }
      return result;
    }

    // Per-id fetches via events.get. Used for sparse lookups (e.g.
    // HideEventDialog opening on a single event). For batch rendering, the
    // hook should prefer enrichByRange.
    await Promise.all(
      eventIds.map(async (eventId) => {
        try {
          const url = `${baseUrl}/calendar/v3/calendars/${encodeURIComponent(
            calendarId,
          )}/events/${encodeURIComponent(eventId)}?fields=id,summary`;
          const response = await fetchImpl(url, {
            method: "GET",
            headers: authHeader(credentials.accessToken),
            signal,
          });
          const fetchedAt = Date.now();
          if (!response.ok) {
            result.set(eventId, {
              title: null,
              fetchedAt,
              error: await classifyErrorWithBody(response),
            });
            return;
          }
          const body = await response.json();
          result.set(eventId, {
            title: typeof body?.summary === "string" ? body.summary : null,
            fetchedAt,
          });
        } catch (err) {
          if (err?.name === "AbortError") throw err;
          result.set(eventId, {
            title: null,
            fetchedAt: Date.now(),
            error: "network",
          });
        }
      }),
    );
    return result;
  }

  async function enrichByRange({ calendarId, timeMin, timeMax, credentials, signal }) {
    const result = new Map();
    if (!credentials?.accessToken) return result;

    try {
      const params = new URLSearchParams({
        timeMin,
        timeMax,
        singleEvents: "true",
        showDeleted: "false",
        maxResults: "2500",
        fields: "items(id,summary)",
      });
      const url = `${baseUrl}/calendar/v3/calendars/${encodeURIComponent(
        calendarId,
      )}/events?${params.toString()}`;
      const response = await fetchImpl(url, {
        method: "GET",
        headers: authHeader(credentials.accessToken),
        signal,
      });
      const fetchedAt = Date.now();
      if (!response.ok) {
        // Range-fetch errors are surfaced empty here. The hook's contract is:
        // any event not present in the returned map should fall back to
        // enrichByIds. The hook layer will decide whether to retry / surface.
        // We log to console.warn so devtools shows the failure during dev.
        const code = await classifyErrorWithBody(response);
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            `[GoogleCalendarEnrichmentProvider] events.list ${calendarId} failed: ${code} (${response.status})`,
          );
        }
        return result;
      }
      const body = await response.json();
      for (const item of body?.items || []) {
        if (item?.id) {
          result.set(item.id, {
            title: typeof item.summary === "string" ? item.summary : null,
            fetchedAt,
          });
        }
      }
      return result;
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      return result;
    }
  }

  return {
    providerId: "google",
    enrichByIds,
    enrichByRange,
  };
}
